// src/features/workspace/TaskSavedViews.tsx
// Saved task views, wired into the workspace toolbar.
//
// This is where a TaskView stops being a row in an admin table and becomes the
// thing you work in: the strip applies a view to the live workspace state, and
// every write (create, overwrite, default, delete) starts from what is on
// screen rather than from a form.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from '@react';
import { useAuth, useI18n, useTaskView } from '@react/hooks';
import { ErrorAlert } from '@react/components';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useWorkspace } from '@/layouts/WorkspaceLayout';
import {
  applySavedViewToState,
  buildSavedViewDisplay,
  buildSavedViewFilter,
  canEditSavedView,
  isSavedViewModified,
  isViewTypeApproximated,
  savedViewFieldMask,
  sortSavedViews,
} from '@/features/workspace/logic';
import { SavedViewsBar, type SavedViewItem } from '@/features/workspace/SavedViewsBar';
import { SaveViewDialog, type SaveViewValues } from '@/features/workspace/SaveViewDialog';

import type { TaskView } from '@core/types/task';

/** URL params that mean "this link already says what to show". */
const WORKSPACE_URL_KEYS = ['view', 'filter', 'group', 'sort', 'dir', 'q', 'saved'];

function urlCarriesWorkspaceState(): boolean {
  const params = new URLSearchParams(window.location.search);
  return WORKSPACE_URL_KEYS.some((key) => params.has(key));
}

export const TaskSavedViews = observer(() => {
  const store = useTaskView();
  const auth = useAuth();
  const { t } = useI18n();
  const { state, defaults, dispatch } = useWorkspace();

  // Views are owned by the profile the caller is acting as — the same id the
  // service stamps into `owner` (see actorFromContext in the task service).
  const profileId = auth.activeProfileId ?? auth.userId ?? null;

  useEffect(() => {
    store.ListViews().catch(() => { /* surfaced through store.error */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const views = useMemo(() => sortSavedViews(store.items ?? []), [store.items]);

  const activeView = useMemo(
    () => views.find((view) => view.entity?.id === state.viewId) ?? null,
    [views, state.viewId],
  );

  const isModified = activeView ? isSavedViewModified(state, activeView, defaults) : false;

  // ── Applying ─────────────────────────────────────────────────────────

  const applyView = useCallback(
    (view: TaskView) => {
      dispatch({ type: 'SET_STATE', state: applySavedViewToState(state, view, defaults) });
    },
    [dispatch, state, defaults],
  );

  const handleSelect = useCallback(
    (id: string | null) => {
      if (!id) {
        // "All tasks" clears the narrowing but keeps density, which is a
        // reading preference rather than part of any view.
        dispatch({ type: 'SET_STATE', state: { ...defaults, density: state.density } });
        return;
      }
      const view = views.find((candidate) => candidate.entity?.id === id);
      if (view) applyView(view);
    },
    [dispatch, defaults, state.density, views, applyView],
  );

  // A default view is what "open the task workspace" should mean — but only
  // when the caller did not arrive with an explicit link. A URL that already
  // carries filters wins over anyone's default.
  const respectDefaultRef = useRef(!urlCarriesWorkspaceState());
  useEffect(() => {
    if (!respectDefaultRef.current || views.length === 0) return;
    respectDefaultRef.current = false;
    const preferred = views.find((view) => view.is_default);
    if (preferred) applyView(preferred);
  }, [views, applyView]);

  // ── Saving ───────────────────────────────────────────────────────────

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TaskView | null>(null);

  const handleSaveCurrent = useCallback(async () => {
    if (!activeView?.entity?.id) return;
    const mask = savedViewFieldMask(state, activeView, defaults);
    if (mask.length === 0) return;

    try {
      await store.UpdateView(activeView.entity.id, {
        view: {
          ...activeView,
          filters: buildSavedViewFilter(state, activeView.filters),
          display: buildSavedViewDisplay(state, activeView.display),
        },
        field_mask: mask,
      });
    } catch {
      // surfaced through store.error
    }
  }, [activeView, state, defaults, store]);

  /**
   * Keep "default" meaning one view. The service stores the flag per view and
   * does not clear the others, so the client that sets it clears the ones it
   * is allowed to; a colleague's shared default is left alone rather than
   * failing the whole action.
   */
  const clearOtherDefaults = useCallback(
    async (keepId: string) => {
      const stale = views.filter(
        (view) =>
          view.is_default &&
          view.entity?.id &&
          view.entity.id !== keepId &&
          canEditSavedView(view, profileId),
      );
      for (const view of stale) {
        try {
          await store.UpdateView(view.entity?.id ?? '', {
            view: { ...view, is_default: false },
            field_mask: ['is_default'],
          });
        } catch {
          // a stale default is cosmetic; do not block the action on it
        }
      }
    },
    [views, profileId, store],
  );

  const handleSaveAs = useCallback(
    async (values: SaveViewValues) => {
      try {
        const created = await store.CreateView({
          view: {
            name: values.name,
            filters: buildSavedViewFilter(state),
            display: buildSavedViewDisplay(state),
            is_shared: values.isShared,
            is_default: values.isDefault,
          },
        });
        setDialogOpen(false);
        if (created?.entity?.id) {
          dispatch({ type: 'SET_STATE', state: { ...state, viewId: created.entity.id } });
          if (values.isDefault) await clearOtherDefaults(created.entity.id);
        }
      } catch {
        // surfaced through store.error; the dialog stays open so the typed
        // name is not lost
      }
    },
    [state, store, dispatch, clearOtherDefaults],
  );

  const handleToggleDefault = useCallback(
    async (id: string) => {
      const view = views.find((candidate) => candidate.entity?.id === id);
      if (!view) return;
      const next = !view.is_default;
      try {
        await store.UpdateView(id, {
          view: { ...view, is_default: next },
          field_mask: ['is_default'],
        });
        if (next) await clearOtherDefaults(id);
      } catch {
        // surfaced through store.error
      }
    },
    [views, store, clearOtherDefaults],
  );

  const handleDelete = useCallback(async () => {
    const id = deleteTarget?.entity?.id;
    if (!id) return;
    try {
      await store.DeleteView(id);
      if (state.viewId === id) {
        dispatch({ type: 'SET_STATE', state: { ...defaults, density: state.density } });
      }
    } catch {
      // surfaced through store.error
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, store, state.viewId, state.density, defaults, dispatch]);

  // ── Render ───────────────────────────────────────────────────────────

  const items = useMemo<SavedViewItem[]>(
    () =>
      views
        .filter((view) => !!view.entity?.id)
        .map((view) => ({
          id: view.entity?.id ?? '',
          name: view.name || t('workspace.savedViews.untitled'),
          isShared: !!view.is_shared,
          isDefault: !!view.is_default,
          canEdit: canEditSavedView(view, profileId),
        })),
    [views, profileId, t],
  );

  const listFailed = !!store.error && !store.isLoading && views.length === 0;
  const filterCount = state.filters.length + (state.search.trim() ? 1 : 0);

  return (
    <div className="space-y-2">
      <SavedViewsBar
        views={items}
        activeId={activeView?.entity?.id ?? null}
        isModified={isModified}
        isLoading={store.isLoading}
        isSaving={store.isSaving || store.isDeleting}
        error={listFailed ? store.error : undefined}
        onSelect={handleSelect}
        onSaveCurrent={handleSaveCurrent}
        onSaveAs={() => setDialogOpen(true)}
        onReset={() => activeView && applyView(activeView)}
        onToggleDefault={handleToggleDefault}
        onDelete={(id) => {
          const view = views.find((candidate) => candidate.entity?.id === id);
          if (view) setDeleteTarget(view);
        }}
        onRetry={() => {
          store.clearError();
          store.ListViews().catch(() => { /* surfaced through store.error */ });
        }}
      />

      {store.error && !listFailed ? (
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
      ) : null}

      <SaveViewDialog
        open={dialogOpen}
        title={t('workspace.savedViews.dialog.title')}
        initial={{
          name: activeView ? t('workspace.savedViews.copyOf', { name: activeView.name }) : '',
          isShared: !!activeView?.is_shared,
          isDefault: false,
        }}
        isSaving={store.isSaving}
        approximatedViewType={isViewTypeApproximated(state.view)}
        filterCount={filterCount}
        onSubmit={handleSaveAs}
        onCancel={() => setDialogOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('workspace.savedViews.deleteTitle')}
        description={t('workspace.savedViews.deleteConfirm', { name: deleteTarget?.name ?? '' })}
        destructive
        isConfirming={store.isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
});

export default TaskSavedViews;
